import { ApiError, post } from '../api';
import { storage } from '../storage';

/**
 * AuthProvider implementations (frontend side).
 * - LocalAuthClient: email/password against our API (development and small deployments).
 * - SupabaseAuthClient: Supabase Auth REST endpoints ONLY (no supabase-js, no Realtime).
 *   The API verifies the Supabase JWT and maps it to our users table.
 */
export interface AuthClient {
  readonly kind: 'local' | 'supabase';
  getToken(): Promise<string | null>;
  login(email: string, password: string): Promise<void>;
  register(input: { fullName: string; email: string; password: string; phone?: string; language: 'fr' | 'en' }): Promise<void>;
  devLogin(email: string): Promise<void>;
  logout(): Promise<void>;
}

const TOKEN_KEY = 'menage.token';

export class LocalAuthClient implements AuthClient {
  readonly kind = 'local' as const;

  async getToken() {
    return storage.get(TOKEN_KEY);
  }

  async login(email: string, password: string) {
    const r = await post<{ token: string }>('/auth/login', { email, password });
    storage.set(TOKEN_KEY, r.token);
  }

  async register(input: { fullName: string; email: string; password: string; phone?: string; language: 'fr' | 'en' }) {
    const r = await post<{ token: string }>('/auth/register', input);
    storage.set(TOKEN_KEY, r.token);
  }

  async devLogin(email: string) {
    const r = await post<{ token: string }>('/auth/dev-login', { email });
    storage.set(TOKEN_KEY, r.token);
  }

  async logout() {
    storage.remove(TOKEN_KEY);
  }
}

interface SupabaseSession {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

export class SupabaseAuthClient implements AuthClient {
  readonly kind = 'supabase' as const;
  private static readonly KEY = 'menage.supabase.session';

  constructor(
    private readonly url: string,
    private readonly anonKey: string,
  ) {
    this.consumeRedirect();
  }

  /** Email confirmation / magic links land on the app with the session in the URL hash. */
  private consumeRedirect() {
    if (typeof window === 'undefined' || !window.location.hash.includes('access_token=')) return;
    const h = new URLSearchParams(window.location.hash.slice(1));
    const access = h.get('access_token');
    const refresh = h.get('refresh_token');
    if (access && refresh) {
      const expiresAt = Number(h.get('expires_at')) || Math.floor(Date.now() / 1000) + Number(h.get('expires_in') ?? 3600);
      storage.set(SupabaseAuthClient.KEY, JSON.stringify({ access_token: access, refresh_token: refresh, expires_at: expiresAt }));
    }
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  private session(): SupabaseSession | null {
    const raw = storage.get(SupabaseAuthClient.KEY);
    try {
      return raw ? (JSON.parse(raw) as SupabaseSession) : null;
    } catch {
      return null;
    }
  }

  private async call(path: string, body: unknown): Promise<SupabaseSession> {
    const res = await fetch(`${this.url}/auth/v1/${path}`, {
      method: 'POST',
      headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new ApiError(401, 'INVALID_CREDENTIALS');
    const s = (await res.json()) as SupabaseSession & { expires_in?: number };
    const session = { ...s, expires_at: s.expires_at ?? Math.floor(Date.now() / 1000) + (s.expires_in ?? 3600) };
    storage.set(SupabaseAuthClient.KEY, JSON.stringify(session));
    return session;
  }

  async getToken() {
    const s = this.session();
    if (!s) return null;
    if (s.expires_at - 60 > Date.now() / 1000) return s.access_token;
    try {
      return (await this.call('token?grant_type=refresh_token', { refresh_token: s.refresh_token })).access_token;
    } catch {
      storage.remove(SupabaseAuthClient.KEY);
      return null;
    }
  }

  async login(email: string, password: string) {
    await this.call('token?grant_type=password', { email, password });
  }

  async register(input: { fullName: string; email: string; password: string }) {
    const redirect = encodeURIComponent(`${window.location.origin}/app`);
    const res = await fetch(`${this.url}/auth/v1/signup?redirect_to=${redirect}`, {
      method: 'POST',
      headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: input.email, password: input.password, data: { full_name: input.fullName } }),
    });
    if (!res.ok) throw new ApiError(res.status, res.status === 422 ? 'EMAIL_TAKEN' : 'INTERNAL');
    const body = (await res.json()) as Partial<SupabaseSession> & { expires_in?: number };
    // Email confirmation enabled: no session until the link in the email is clicked.
    if (!body.access_token) throw new ApiError(0, 'CONFIRM_EMAIL');
    storage.set(SupabaseAuthClient.KEY, JSON.stringify({ ...body, expires_at: body.expires_at ?? Math.floor(Date.now() / 1000) + (body.expires_in ?? 3600) }));
    await post('/auth/provision', { fullName: input.fullName });
  }

  async devLogin() {
    throw new Error('dev_login_unavailable');
  }

  async logout() {
    storage.remove(SupabaseAuthClient.KEY);
  }
}

/**
 * Transition mode (VITE_LOCAL_AUTH_FALLBACK=true): Supabase first, local accounts as a
 * fallback (demo accounts, DEV role switcher). The API must allow it too (LOCAL_AUTH_FALLBACK).
 */
export class HybridAuthClient implements AuthClient {
  readonly kind = 'supabase' as const;
  constructor(
    private readonly supabase: SupabaseAuthClient,
    private readonly local: LocalAuthClient,
  ) {}

  async getToken() {
    return (await this.local.getToken()) ?? this.supabase.getToken();
  }

  async login(email: string, password: string) {
    try {
      await this.supabase.login(email, password);
      await this.local.logout();
    } catch {
      await this.local.login(email, password);
    }
  }

  async register(input: { fullName: string; email: string; password: string; phone?: string; language: 'fr' | 'en' }) {
    await this.local.logout();
    await this.supabase.register(input);
  }

  async devLogin(email: string) {
    await this.supabase.logout();
    await this.local.devLogin(email);
  }

  async logout() {
    await this.local.logout();
    await this.supabase.logout();
  }
}

export function createAuthClient(): AuthClient {
  const env = import.meta.env;
  if (env.VITE_AUTH_PROVIDER === 'supabase' && env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY) {
    const supabase = new SupabaseAuthClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY);
    return env.VITE_LOCAL_AUTH_FALLBACK === 'true' ? new HybridAuthClient(supabase, new LocalAuthClient()) : supabase;
  }
  return new LocalAuthClient();
}
